import { useState } from 'react';
import { addDays, todayIn } from '@njpos/core';
import { api, sizeLabel, usd, usd0 } from '../lib/api';
import { ErrorNote, useLoad } from '../components/ui';

type Tab = 'trends' | 'movers' | 'sellers' | 'brands' | 'customers' | 'pairs' | 'reorder';
const TABS: [Tab, string][] = [['trends', 'Trends and busy times'], ['movers', 'Up and down'], ['sellers', 'Best and slowest sellers'], ['brands', 'Brands and distributors'], ['customers', 'Customers'], ['pairs', 'Bought together'], ['reorder', 'When to reorder']];
const name = (i: any) => `${i.brand} ${i.name}${i.size_ml ? ` · ${sizeLabel(i.size_ml)}` : ''}`;
const label = (c: string) => c.replace('_', ' ');
const pctChange = (now: number, before: number) => (before ? `${now >= before ? '▲' : '▼'} ${Math.abs(Math.round(((now - before) / before) * 100))}%` : '');
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const hourLabel = (h: number) => (h === 0 ? '12a' : h < 12 ? `${h}a` : h === 12 ? '12p' : `${h - 12}p`);

// What the numbers say: when you are busy, what sells and what does not, who buys, and what to order.
export default function Insights() {
  const today = todayIn();
  const [tab, setTab] = useState<Tab>('trends');
  const [from, setFrom] = useState(addDays(today, -29));
  const [to, setTo] = useState(today);
  const preset = (days: number) => { setFrom(addDays(today, -days + 1)); setTo(today); };
  return (
    <div className="stack">
      <div className="page-head">
        <div className="grow"><h1>AI Sales Insights</h1><p>Where the money comes from and what to do about it. Pick the dates, then a view.</p></div>
        {tab !== 'reorder' && <><label>From<input type="date" value={from} onChange={(e) => e.target.value && setFrom(e.target.value)} /></label>
          <label>To<input type="date" value={to} onChange={(e) => e.target.value && setTo(e.target.value)} /></label></>}
      </div>
      {tab !== 'reorder' && <div className="row wrap">{[[7, 'Last 7 days'], [30, 'Last 30 days'], [90, 'Last 90 days'], [365, 'Last year']].map(([d, l]) => <button key={d as number} onClick={() => preset(d as number)}>{l}</button>)}</div>}
      <div className="row wrap" role="tablist">{TABS.map(([k, l]) => <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'primary' : ''} onClick={() => setTab(k)}>{l}</button>)}</div>
      {tab === 'trends' && <Trends from={from} to={to} />}
      {tab === 'movers' && <Movers from={from} to={to} />}
      {tab === 'sellers' && <Sellers from={from} to={to} />}
      {tab === 'brands' && <Brands from={from} to={to} />}
      {tab === 'customers' && <Customers from={from} to={to} />}
      {tab === 'pairs' && <Pairs from={from} to={to} />}
      {tab === 'reorder' && <Reorder />}
    </div>
  );
}

// A simple bar chart: this period's days as bars, the period before as a line.
function Bars({ series }: { series: any[] }) {
  const W = 720, H = 190, pad = 28;
  const max = Math.max(1, ...series.flatMap((s) => [s.net_cents, s.prev_net_cents]));
  const bw = (W - pad) / Math.max(1, series.length);
  const y = (v: number) => H - 22 - (v / max) * (H - 40);
  const line = series.map((s, i) => `${i ? 'L' : 'M'}${pad + i * bw + bw / 2},${y(s.prev_net_cents)}`).join('');
  const step = Math.max(1, Math.ceil(series.length / 8));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Sales by day" style={{ width: '100%', height: 'auto' }}>
      <text x={0} y={12} fontSize="10" fill="currentColor" opacity="0.6">{usd0(max)}</text>
      {series.map((s, i) => (
        <g key={s.date}>
          <rect x={pad + i * bw + 1} y={y(s.net_cents)} width={Math.max(1, bw - 2)} height={H - 22 - y(s.net_cents)} fill="var(--accent, #2563eb)" opacity="0.85"><title>{`${s.date}: ${usd(s.net_cents)} (${s.sales} sales). Before: ${usd(s.prev_net_cents)}`}</title></rect>
          {i % step === 0 && <text x={pad + i * bw + bw / 2} y={H - 6} fontSize="10" textAnchor="middle" fill="currentColor" opacity="0.6">{s.date.slice(5)}</text>}
        </g>))}
      <path d={line} fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="4 3" opacity="0.55" />
    </svg>
  );
}

function Trends({ from, to }: { from: string; to: string }) {
  const r = useLoad(() => api<any>(`/api/analytics/trends?from=${from}&to=${to}`), [from, to]);
  const d = r.data;
  const cell = new Map<string, any>((d?.heat ?? []).map((h: any) => [`${h.dow}-${h.hour}`, h]));
  const maxNet = Math.max(1, ...(d?.heat ?? []).map((h: any) => h.net_cents));
  const hours = Array.from({ length: 24 }, (_, h) => h).filter((h) => (d?.heat ?? []).some((x: any) => x.hour === h));
  const bestDay = d?.series.length ? [...d.series].sort((a: any, b: any) => b.net_cents - a.net_cents)[0] : null;
  return (
    <div className="stack">
      <ErrorNote error={r.error} />
      {d && (
        <>
          <div className="stats">
            <div><div className="v">{usd(d.totals.net_cents)}</div><div className="l">Net sales {pctChange(d.totals.net_cents, d.totals.prev_net_cents)} <span className="faint">vs the {d.series.length} days before</span></div></div>
            <div><div className="v">{d.totals.sales}</div><div className="l">Sales {pctChange(d.totals.sales, d.totals.prev_sales)}</div></div>
            <div><div className="v">{usd(d.totals.profit_cents)}</div><div className="l">Gross profit</div></div>
            <div><div className="v">{bestDay ? usd(bestDay.net_cents) : '—'}</div><div className="l">Best day{bestDay ? `, ${bestDay.date}` : ''}</div></div>
          </div>
          <div className="panel"><div className="panel-head"><h2>Sales by day</h2><span className="faint">Bars: these days. Dashed line: the same number of days before.</span></div><div className="panel-body"><Bars series={d.series} /></div></div>
          <div className="panel"><div className="panel-head"><h2>When you are busy</h2><span className="faint">Darker means more sales. Hover for the numbers.</span></div>
            <div className="panel-body" style={{ overflowX: 'auto' }}>
              {d.heat.length === 0 ? <div className="empty">No sales in this period.</div> : (
                <table style={{ borderCollapse: 'separate', borderSpacing: 2, fontSize: 12 }}>
                  <thead><tr><th />{hours.map((h) => <th key={h} style={{ fontWeight: 400, opacity: 0.6 }}>{hourLabel(h)}</th>)}</tr></thead>
                  <tbody>{DOW.map((dn, dow) => (
                    <tr key={dn}><th style={{ fontWeight: 500, textAlign: 'right', paddingRight: 6 }}>{dn}</th>
                      {hours.map((h) => { const c = cell.get(`${dow}-${h}`); return <td key={h} title={c ? `${dn} ${hourLabel(h)}: ${usd(c.net_cents)} from ${c.receipts} sales` : 'no sales'} style={{ width: 30, height: 24, borderRadius: 3, background: c ? `color-mix(in srgb, var(--accent, #2563eb) ${Math.max(10, Math.round((c.net_cents / maxNet) * 100))}%, transparent)` : 'rgba(128,128,128,0.08)' }} />; })}</tr>))}</tbody>
                </table>)}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ItemTable({ rows, extra }: { rows: any[]; extra: { head: string; cell: (r: any) => string }[] }) {
  return (
    <div className="table-wrap"><table className="grid">
      <thead><tr><th>Item</th>{extra.map((e) => <th key={e.head} className="n">{e.head}</th>)}</tr></thead>
      <tbody>{rows.map((r) => <tr key={r.id}><td>{name(r)}</td>{extra.map((e) => <td key={e.head} className="n">{e.cell(r)}</td>)}</tr>)}</tbody>
    </table>{rows.length === 0 && <div className="empty">Nothing to show.</div>}</div>
  );
}

function Sellers({ from, to }: { from: string; to: string }) {
  const r = useLoad(() => api<any>(`/api/analytics/sellers?from=${from}&to=${to}`), [from, to]);
  const d = r.data;
  return (
    <div className="stack">
      <ErrorNote error={r.error} />
      {d && (
        <>
          <div className="notice">{d.deadCount} items on your shelf did not sell at all in this period, with {usd(d.deadTiedUpCents)} of stock (at cost) sitting still. Consider a promotion, a bundle, or not reordering.</div>
          <div className="grid-2" style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))' }}>
            <div className="panel"><div className="panel-head"><h2>Most bottles sold</h2></div><ItemTable rows={d.byUnits} extra={[{ head: 'Units', cell: (r) => String(r.units) }, { head: 'Sales', cell: (r) => usd(r.net_cents) }, { head: 'On hand', cell: (r) => String(r.on_hand) }]} /></div>
            <div className="panel"><div className="panel-head"><h2>Most profit</h2></div><ItemTable rows={d.byProfit} extra={[{ head: 'Profit', cell: (r) => usd(r.profit_cents) }, { head: 'Units', cell: (r) => String(r.units) }, { head: 'Margin', cell: (r) => (r.net_cents ? `${Math.round((r.profit_cents / r.net_cents) * 100)}%` : '—') }]} /></div>
            <div className="panel"><div className="panel-head"><h2>Selling slowly</h2><span className="faint">Sold, but least, with stock on the shelf</span></div><ItemTable rows={d.slow} extra={[{ head: 'Units', cell: (r) => String(r.units) }, { head: 'On hand', cell: (r) => String(r.on_hand) }]} /></div>
            <div className="panel"><div className="panel-head"><h2>Not selling at all</h2><span className="faint">Biggest money tied up first</span></div><ItemTable rows={d.dead} extra={[{ head: 'On hand', cell: (r) => String(r.on_hand) }, { head: 'Tied up', cell: (r) => usd(r.tied_up_cents) }]} /></div>
          </div>
        </>
      )}
    </div>
  );
}

function Brands({ from, to }: { from: string; to: string }) {
  const r = useLoad(() => api<any>(`/api/analytics/brands?from=${from}&to=${to}`), [from, to]);
  const d = r.data;
  const table = (title: string, rows: any[]) => (
    <div className="panel"><div className="panel-head"><h2>{title}</h2></div><div className="table-wrap"><table className="grid">
      <thead><tr><th>Name</th><th className="n">Items</th><th className="n">Units</th><th className="n">Sales</th><th className="n">Profit</th><th className="n">Margin</th></tr></thead>
      <tbody>{rows.map((b) => <tr key={b.name}><td>{b.name}</td><td className="n">{b.items}</td><td className="n">{b.units}</td><td className="n">{usd(b.net_cents)}</td><td className="n">{usd(b.profit_cents)}</td><td className="n">{b.margin_pct}%</td></tr>)}</tbody>
    </table>{rows.length === 0 && <div className="empty">No sales in this period.</div>}</div></div>
  );
  return <div className="stack"><ErrorNote error={r.error} />{d && <>{table('Brands', d.brands)}{table('Distributors (the one set on each item)', d.distributors)}</>}</div>;
}

function Customers({ from, to }: { from: string; to: string }) {
  const r = useLoad(() => api<any>(`/api/analytics/customers?from=${from}&to=${to}`), [from, to]);
  const d = r.data;
  return (
    <div className="stack">
      <ErrorNote error={r.error} />
      {d && (
        <>
          <div className="stats">
            <div><div className="v">{d.receipts ? Math.round((d.memberReceipts / d.receipts) * 100) : 0}%</div><div className="l">Of sales had a customer attached</div></div>
            <div><div className="v">{usd(d.avgMemberCents)}</div><div className="l">Average sale with a customer</div><div className="faint">without: {usd(d.avgOtherCents)}</div></div>
            <div><div className="v">{d.newCustomers} / {d.returningCustomers}</div><div className="l">New / returning customers</div></div>
            <div><div className="v">{d.lapsedCustomers}</div><div className="l">Regulars not seen in 60 days</div></div>
          </div>
          <div className="panel"><div className="panel-head"><h2>Best customers</h2></div><div className="table-wrap"><table className="grid">
            <thead><tr><th>Customer</th><th>Group</th><th className="n">Visits</th><th className="n">Spent</th><th>Last visit</th></tr></thead>
            <tbody>{d.top.map((c: any) => <tr key={c.id}><td>{c.name} {c.club && <span className="chip good">Club</span>}</td><td>{c.grp ?? ''}</td><td className="n">{c.visits}</td><td className="n">{usd(c.net_cents)}</td><td>{c.last_visit}</td></tr>)}</tbody>
          </table>{d.top.length === 0 && <div className="empty">No sales with a customer attached yet. Pick a customer at the register to build this view.</div>}</div></div>
        </>
      )}
    </div>
  );
}

function Pairs({ from, to }: { from: string; to: string }) {
  const r = useLoad(() => api<any>(`/api/analytics/pairs?from=${from}&to=${to}`), [from, to]);
  return (
    <div className="stack">
      <p className="muted">Items that were rung up in the same sale again and again. Good ideas for a bundle, a shelf next to each other, or a combined promotion.</p>
      <ErrorNote error={r.error} />
      <div className="panel"><div className="table-wrap"><table className="grid">
        <thead><tr><th>Item</th><th>Often with</th><th className="n">Sales together</th></tr></thead>
        <tbody>{r.data?.pairs.map((p: any, i: number) => <tr key={i}><td>{name(p.a)}</td><td>{name(p.b)}</td><td className="n">{p.together}</td></tr>)}</tbody>
      </table>{r.data?.pairs.length === 0 && <div className="empty">Not enough repeat combinations yet in this period.</div>}</div></div>
    </div>
  );
}

function Reorder() {
  const r = useLoad(() => api<any>('/api/analytics/reorder'), []);
  const d = r.data;
  return (
    <div className="stack">
      <p className="muted">From how fast each item sold in the last 28 days: how many days the stock will last, and how much to order to cover about a month.</p>
      <ErrorNote error={r.error} />
      <div className="panel"><div className="table-wrap"><table className="grid">
        <thead><tr><th>Item</th><th>Vendor</th><th className="n">Sells / day</th><th className="n">On hand</th><th className="n">Days left</th><th className="n">Order (month)</th><th className="n">Cost</th></tr></thead>
        <tbody>{d?.items.map((i: any) => (
          <tr key={i.id}><td>{name(i)}</td><td>{i.distributor ?? '—'}</td><td className="n">{i.per_day}</td><td className="n">{i.on_hand}</td>
            <td className="n"><span className={`chip ${i.days_left <= 3 ? 'bad' : 'warn'}`}>{i.days_left}</span></td>
            <td className="n">{i.order_units > 0 ? `${i.order_units}${i.order_cases ? ` (${i.order_cases} case${i.order_cases === 1 ? '' : 's'})` : ''}` : '—'}</td><td className="n">{i.order_cost_cents ? usd(i.order_cost_cents) : '—'}</td></tr>))}</tbody>
      </table>{d?.items.length === 0 && <div className="empty">Nothing is running low, based on the last 28 days.</div>}</div></div>
      <p className="faint">Sales speed is an average, so a holiday or a promotion can change it. Treat this as a starting point for the Buy list, not an order.</p>
    </div>
  );
}

// What is selling more and what is selling less than in the period just before this one.
function Movers({ from, to }: { from: string; to: string }) {
  const r = useLoad(() => api<any>(`/api/analytics/movers?from=${from}&to=${to}`), [from, to]);
  const d = r.data;
  const arrow = (n: number) => (n > 0 ? '▲' : n < 0 ? '▼' : '');
  const col = (n: number) => (n > 0 ? 'var(--good)' : n < 0 ? 'var(--bad)' : undefined);
  const pct = (p: number | null) => (p == null ? 'new' : `${p > 0 ? '+' : ''}${p}%`);
  const table = (title: string, rows: any[], note: string) => (
    <div className="panel"><div className="panel-head"><h2>{title}</h2><span className="faint">{note}</span></div><div className="table-wrap"><table className="grid">
      <thead><tr><th>Item</th><th className="n">Before</th><th className="n">Now</th><th className="n">Change</th><th className="n">Sales change</th><th className="n">On hand</th></tr></thead>
      <tbody>{rows.map((i) => (
        <tr key={i.id}><td>{name(i)}</td><td className="n">{i.prev_units}</td><td className="n">{i.units}</td>
          <td className="n" style={{ color: col(i.change_units) }}>{arrow(i.change_units)} {Math.abs(i.change_units)} ({pct(i.change_pct)})</td>
          <td className="n" style={{ color: col(i.change_net_cents) }}>{usd(i.change_net_cents)}</td><td className="n">{i.on_hand}</td></tr>))}</tbody>
    </table>{rows.length === 0 && <div className="empty">Nothing here.</div>}</div></div>
  );
  return (
    <div className="stack">
      <ErrorNote error={r.error} />
      {d && (
        <>
          <p className="muted">Compares {d.from} to {d.to} with the same number of days just before ({d.previous.from} to {d.previous.to}). Items that sold only a couple of bottles either way are left out, since that is just chance.</p>
          <div className="stats">
            <div><div className="v">{d.totals.units}</div><div className="l">Bottles sold <span style={{ color: col(d.totals.units - d.totals.prev_units) }}>{arrow(d.totals.units - d.totals.prev_units)} {Math.abs(d.totals.units - d.totals.prev_units)}</span></div></div>
            <div><div className="v">{usd(d.totals.net_cents)}</div><div className="l">Sales <span style={{ color: col(d.totals.net_cents - d.totals.prev_net_cents) }}>{arrow(d.totals.net_cents - d.totals.prev_net_cents)} {usd(Math.abs(d.totals.net_cents - d.totals.prev_net_cents))}</span></div></div>
          </div>
          <div className="panel"><div className="panel-head"><h2>By category</h2></div><div className="table-wrap"><table className="grid">
            <thead><tr><th>Category</th><th className="n">Before</th><th className="n">Now</th><th className="n">Change</th><th className="n">Sales change</th></tr></thead>
            <tbody>{d.categories.map((c: any) => (
              <tr key={c.category}><td>{label(c.category)}</td><td className="n">{c.prev_units}</td><td className="n">{c.units}</td>
                <td className="n" style={{ color: col(c.change_units) }}>{arrow(c.change_units)} {Math.abs(c.change_units)} ({pct(c.change_pct)})</td><td className="n" style={{ color: col(c.change_net_cents) }}>{usd(c.change_net_cents)}</td></tr>))}</tbody>
          </table></div></div>
          <div className="grid-2" style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))' }}>
            {table('Selling more ▲', d.up, 'Biggest gains in bottles')}
            {table('Selling less ▼', d.down, 'Biggest drops in bottles')}
            {table('Started selling', d.started, 'Not sold in the earlier period')}
            {table('Stopped selling', d.stopped, 'Sold before, nothing now')}
          </div>
        </>
      )}
    </div>
  );
}
